import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MessageSquare, Search, Star } from 'lucide-react';
import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import ColumnFilterBuilder, {
  type FilterRule,
  emptyRule, isRuleComplete,
} from '../components/ColumnFilterBuilder';
import {
  getAllAlumni, toggleAlumniStar,
  getAlumniComments, createAlumniComment, updateAlumniComment, deleteAlumniComment,
} from '../services/apiService';
import type { TFunction } from 'i18next';

// ── Types ───────────────────────────────────────────────────────────────────

interface AlumniEducation {
  schoolName?: string;
  fieldOfStudy?: string;
  degreeName?: string;
  startDate?: string;
  endDate?: string;
}

interface AlumniExperience {
  position?: string;
  companyName?: string;
  duration?: string;
  startDate?: string;
  endDate?: string;
}

interface AlumniData {
  id: number;
  firstName?: string;
  lastName?: string;
  currentCompany?: string;
  location?: string;
  headline?: string;
  about?: string;
  linkedinUrl?: string;
  connectionsCount?: number;
  followerCount?: number;
  starred?: boolean;
  commentCount?: number;
  tags?: string[];
  experience?: AlumniExperience[];
  education?: AlumniEducation;
  [key: string]: any;
}

interface AlumniComment {
  id: number;
  alumniId: number;
  text: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
}

interface ColumnDef {
  key: string;
  type: 'string' | 'number';
}

interface SortConfig {
  key: string | null;
  direction: 'asc' | 'desc';
}

interface EditingComment {
  id: number;
  text: string;
}

interface TagColor {
  bg: string;
  text: string;
  dot: string;
}

const ROWS_PER_PAGE = 50;

// ── Tag color system ─────────────────────────────────────────────────────────

const TAG_PALETTE: TagColor[] = [
  { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },   // blue
  { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },   // red
  { bg: '#dcfce7', text: '#166534', dot: '#22c55e' },   // green
  { bg: '#fef9c3', text: '#854d0e', dot: '#eab308' },   // yellow
  { bg: '#f3e8ff', text: '#6b21a8', dot: '#a855f7' },   // purple
  { bg: '#ffedd5', text: '#9a3412', dot: '#f97316' },   // orange
  { bg: '#ccfbf1', text: '#115e59', dot: '#14b8a6' },   // teal
  { bg: '#ffe4e6', text: '#9f1239', dot: '#fb7185' },   // rose
  { bg: '#ffe4e6', text: '#9f1239', dot: '#fb7185' },   // rose
];

const TAG_COLOR_MAP: Record<string, TagColor> = {
  'ceo':             TAG_PALETTE[0], // blue
  'c-level':         TAG_PALETTE[1], // red
  'founder':         TAG_PALETTE[2], // green
  'decision maker':  TAG_PALETTE[3], // yellow
  'Big company':     TAG_PALETTE[4], // purple
  'investor':        TAG_PALETTE[5], // orange
};

function getTagColor(tag: string): TagColor {
  return TAG_COLOR_MAP[tag] || TAG_PALETTE[6]; // teal fallback
}

// ── Existing helpers ─────────────────────────────────────────────────────────

// ── Diacritics-insensitive normalization ──────────────────────────────────────

function normalize(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}


const COLUMN_DEFS: ColumnDef[] = [
  { key: 'firstName', type: 'string' },
  { key: 'lastName', type: 'string' },
  { key: 'currentCompany', type: 'string' },
  { key: 'position', type: 'string' },
  { key: 'location', type: 'string' },
  { key: 'connectionsCount', type: 'number' },
  { key: 'followerCount', type: 'number' },
  { key: 'endYear', type: 'number' },
];

const COL_SPAN = 13;

// ── Tag-to-keyword mapping (substring matching) ─────────────────────────────

const TAG_KEYWORDS: Record<string, string[]> = {
  'ceo': ['ceo', 'chief executive officer'],
  'c-level': [
    'cto', 'cfo', 'coo', 'cmo', 'cio', 'ciso', 'cpo',
    'chief technology officer', 'chief financial officer',
    'chief operating officer', 'chief marketing officer',
    'chief information officer', 'chief product officer',
  ],
  'founder': ['founder', 'co-founder', 'cofounder', 'co founder', 'founding'],
  'decision maker': [
    'vp', 'vice president', 'director', 'head of', 'president',
    'managing partner', 'managing director', 'principal',
  ],
  'Big company': [
    'meta', 'facebook', 'google', 'alphabet', 'amazon', 'apple',
    'microsoft', 'netflix', 'nvidia', 'tesla', 'salesforce',
    'oracle', 'IBM', 'intel', 'cisco', 'stripe', 'uber', 'airbnb', 'yandex',
  ],
  'investor': [
    'investor', 'angel investor', 'business angel',
    'venture capital', 'vc', 'private equity',
    'investment committee', 'shareholder', 'seed fund',
  ],
};

function highlightByTags(text: string | undefined | null, tags: string[] | undefined): React.ReactNode {
  if (!text || !tags || tags.length === 0) return text;

  // Collect all { keyword, tag } pairs relevant to this alumnus's tags
  const patterns = [];
  for (const tag of tags) {
    const keywords = TAG_KEYWORDS[tag];
    if (!keywords) continue;
    for (const kw of keywords) {
      patterns.push({ keyword: kw, tag });
    }
  }
  if (patterns.length === 0) return text;

  // Sort longest keyword first so longer matches win
  patterns.sort((a, b) => b.keyword.length - a.keyword.length);

  // Build a single regex with word boundaries for each keyword
  // Escape special regex chars, then join with alternation
  const escaped = patterns.map(p => ({
    ...p,
    re: p.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  }));
  const matches = [];
  for (const { re, tag } of escaped) {
    const regex = new RegExp(`\\b${re}\\b`, 'gi');
    let m;
    while ((m = regex.exec(text)) !== null) {
      matches.push({ start: m.index, end: m.index + m[0].length, tag });
    }
  }

  // Sort by position, then prefer longer spans
  matches.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));

  // Remove overlaps (greedy: earliest & longest wins)
  const kept = [];
  let lastEnd = 0;
  for (const m of matches) {
    if (m.start >= lastEnd) {
      kept.push(m);
      lastEnd = m.end;
    }
  }
  if (kept.length === 0) return text;

  // Build JSX with colored underlines
  const parts = [];
  let cursor = 0;
  for (const m of kept) {
    if (m.start > cursor) parts.push(text.slice(cursor, m.start));
    const c = getTagColor(m.tag);
    parts.push(
      <span key={m.start} style={{
        textDecoration: 'underline',
        textDecorationColor: c.dot,
        textDecorationThickness: '2px',
        textUnderlineOffset: '2px',
      }}>
        {text.slice(m.start, m.end)}
      </span>
    );
    cursor = m.end;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

function getEndYear(education: AlumniEducation | undefined): string {
  if (!education || !education.endDate) return '';
  const match = String(education.endDate).match(/\d{4}/);
  return match ? match[0] : education.endDate;
}

function getCurrentPosition(experience: AlumniExperience[] | undefined): string {
  if (!Array.isArray(experience) || experience.length === 0) return '';
  return experience[0].position || '';
}

function getColumnValue(alumnus: AlumniData, columnKey: string): any {
  switch (columnKey) {
    case 'position': return getCurrentPosition(alumnus.experience);
    case 'endYear': return getEndYear(alumnus.education);
    default: return alumnus[columnKey];
  }
}


function evaluateRule(alumnus: AlumniData, rule: FilterRule): boolean {
  const colDef = COLUMN_DEFS.find(c => c.key === rule.column);
  if (!colDef) return true;

  const rawValue = getColumnValue(alumnus, rule.column);

  if (colDef.type === 'number') {
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return rule.operator === '!=';
    }
    const numItem = Number(rawValue) || 0;
    const numRule = Number(rule.value) || 0;
    switch (rule.operator) {
      case '=': return numItem === numRule;
      case '!=': return numItem !== numRule;
      case '<': return numItem < numRule;
      case '>': return numItem > numRule;
      default: return true;
    }
  } else {
    const strItem = normalize(String(rawValue || ''));
    const strRule = normalize(String(rule.value || ''));
    switch (rule.operator) {
      case '=': return strItem.includes(strRule);
      case '!=': return !strItem.includes(strRule);
      default: return true;
    }
  }
}

function applyRules(data: AlumniData[], rules: FilterRule[]): AlumniData[] {
  const active = rules.filter(isRuleComplete);
  if (active.length === 0) return data;

  return data.filter(item => {
    let result = evaluateRule(item, active[0]);
    for (let i = 1; i < active.length; i++) {
      const ruleResult = evaluateRule(item, active[i]);
      if (active[i].logic === 'OR') {
        result = result || ruleResult;
      } else {
        result = result && ruleResult;
      }
    }
    return result;
  });
}


// ── Global search (all fields, diacritics-insensitive) ───────────────────────

function buildSearchableText(a: AlumniData): string {
  const parts = [
    a.firstName, a.lastName, a.currentCompany, a.location,
    a.headline, a.about, a.linkedinUrl,
    getCurrentPosition(a.experience),
    getEndYear(a.education),
    ...(a.tags || []),
  ];
  // Include all experience entries
  if (Array.isArray(a.experience)) {
    for (const e of a.experience) {
      parts.push(e.position, e.companyName, e.duration, e.startDate, e.endDate);
    }
  }
  // Include education fields
  const edu = a.education || {};
  parts.push(edu.schoolName, edu.fieldOfStudy, edu.degreeName, edu.startDate, edu.endDate);
  return normalize(parts.filter(Boolean).join(' '));
}

function matchesGlobalSearch(alumnus: AlumniData, searchNormalized: string): boolean {
  if (!searchNormalized) return true;
  const haystack = buildSearchableText(alumnus);
  return searchNormalized.split(/\s+/).every(term => haystack.includes(term));
}

// ── Tag Bar ──────────────────────────────────────────────────────────────────

function TagBar({ allTags, activeTagFilters, onToggle, t }: {
  allTags: string[];
  activeTagFilters: Set<string>;
  onToggle: (tag: string) => void;
  t: TFunction;
}) {
  if (allTags.length === 0) return null;

  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted mb-2">
        {t('tags.filterLabel')}
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
        {allTags.map(tag => {
          const c = getTagColor(tag);
          const active = activeTagFilters.has(tag);
          return (
            <Badge
              key={tag}
              variant="outline"
              role="button"
              tabIndex={0}
              onClick={() => onToggle(tag)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle(tag);
                }
              }}
              className={cn(
                'cursor-pointer gap-1.5 border-[1.5px] px-2.5 py-0.5 transition-colors',
                active ? 'font-semibold' : 'font-normal',
              )}
              style={{
                borderColor: c.dot,
                backgroundColor: active ? c.bg : '#ffffff',
                color: active ? c.text : '#495057',
              }}
            >
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: c.dot }}
              />
              {tag}
            </Badge>
          );
        })}
      </div>
    </div>
  );
}

// ── Comments Panel ───────────────────────────────────────────────────────────

function CommentsPanel({
  alumniId, comments, loading, t,
  onAdd, onUpdate, onDelete,
  editingComment, setEditingComment,
  addingCommentFor, setAddingCommentFor,
  newCommentText, setNewCommentText,
  currentUserId,
}: {
  alumniId: number;
  comments: AlumniComment[];
  loading: boolean;
  t: TFunction;
  onAdd: (alumniId: number, text: string) => void;
  onUpdate: (commentId: number, text: string) => void;
  onDelete: (commentId: number, alumniId: number) => void;
  editingComment: EditingComment | null;
  setEditingComment: (c: EditingComment | null) => void;
  addingCommentFor: number | null;
  setAddingCommentFor: (id: number | null) => void;
  newCommentText: string;
  setNewCommentText: (text: string) => void;
  currentUserId: string;
}) {
  const isAdding = addingCommentFor === alumniId;

  return (
    <div className="w-[280px] shrink-0 border-l border-border pl-4 flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <span className="font-semibold text-sm text-foreground">{t('comments.title')}</span>
        {!isAdding && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => { setAddingCommentFor(alumniId); setNewCommentText(''); }}
          >+</Button>
        )}
      </div>

      {/* Add form */}
      {isAdding && (
        <div className="flex flex-col gap-1">
          <Textarea
            value={newCommentText}
            onChange={e => setNewCommentText(e.target.value)}
            placeholder={t('comments.placeholder')}
            rows={3}
            className="text-sm min-h-[60px] resize-y"
          />
          <div className="flex gap-1">
            <Button
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => { onAdd(alumniId, newCommentText); setAddingCommentFor(null); }}
              disabled={!newCommentText.trim()}
            >{t('comments.save')}</Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => setAddingCommentFor(null)}
            >{t('comments.cancel')}</Button>
          </div>
        </div>
      )}

      {loading && <div className="text-xs text-muted-foreground">...</div>}

      {!loading && comments.length === 0 && !isAdding && (
        <div className="text-xs text-muted-foreground italic">{t('comments.empty')}</div>
      )}

      {comments.map(c => {
        const isEditing = editingComment && editingComment.id === c.id;
        const isOwner = c.createdBy === currentUserId;
        return (
          <div key={c.id} className="bg-white border border-border rounded-md p-2 text-xs relative group">
            {isEditing ? (
              <div className="flex flex-col gap-1">
                <Textarea
                  value={editingComment.text}
                  onChange={e => setEditingComment({ ...editingComment, text: e.target.value })}
                  rows={2}
                  className="text-sm min-h-[40px] resize-y"
                />
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => { onUpdate(c.id, editingComment.text); setEditingComment(null); }}
                  >{t('comments.save')}</Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setEditingComment(null)}
                  >{t('comments.cancel')}</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="leading-snug mb-1">{c.text}</div>
                <div className="text-[0.7rem] text-muted-foreground">
                  {c.createdByName || c.createdBy} &middot; {new Date(c.createdAt).toLocaleDateString()}
                </div>
                {isOwner && (
                  <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditingComment({ id: c.id, text: c.text })}
                      title={t('comments.edit')}
                      className="bg-transparent border-none cursor-pointer text-xs text-primary p-0.5 hover:opacity-80"
                    >&#9998;</button>
                    <button
                      onClick={() => { if (window.confirm(t('comments.confirmDelete'))) onDelete(c.id, alumniId); }}
                      title={t('comments.delete')}
                      className="bg-transparent border-none cursor-pointer text-xs text-destructive p-0.5 hover:opacity-80"
                    >&#10005;</button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Expanded Content ─────────────────────────────────────────────────────────

function ExpandedContent({
  alumnus, t,
  comments, loadingComments,
  onAddComment, onUpdateComment, onDeleteComment,
  editingComment, setEditingComment,
  addingCommentFor, setAddingCommentFor,
  newCommentText, setNewCommentText,
  currentUserId,
}: {
  alumnus: AlumniData;
  t: TFunction;
  comments: AlumniComment[];
  loadingComments: boolean;
  onAddComment: (alumniId: number, text: string) => void;
  onUpdateComment: (commentId: number, text: string) => void;
  onDeleteComment: (commentId: number, alumniId: number) => void;
  editingComment: EditingComment | null;
  setEditingComment: (c: EditingComment | null) => void;
  addingCommentFor: number | null;
  setAddingCommentFor: (id: number | null) => void;
  newCommentText: string;
  setNewCommentText: (text: string) => void;
  currentUserId: string;
}) {
  const exp = alumnus.experience || [];
  const edu = alumnus.education || {};
  const tags = alumnus.tags || [];

  const hasContent = tags.length > 0 || alumnus.headline || alumnus.about || edu.schoolName || exp.length > 0;

  if (!hasContent) {
    return (
      <TableCell colSpan={COL_SPAN} className="px-5 py-4 bg-muted/50 text-secondary-foreground">
        <div className="flex gap-4">
          <div className="flex-1 text-muted-foreground italic">{t('detail.noInfo')}</div>
          <CommentsPanel
            alumniId={alumnus.id} comments={comments} loading={loadingComments} t={t}
            onAdd={onAddComment} onUpdate={onUpdateComment} onDelete={onDeleteComment}
            editingComment={editingComment} setEditingComment={setEditingComment}
            addingCommentFor={addingCommentFor} setAddingCommentFor={setAddingCommentFor}
            newCommentText={newCommentText} setNewCommentText={setNewCommentText}
            currentUserId={currentUserId}
          />
        </div>
      </TableCell>
    );
  }

  return (
    <TableCell colSpan={COL_SPAN} className="px-5 py-4 bg-muted/50 text-secondary-foreground">
      <div className="flex gap-4">
        {/* Left: existing content */}
        <div className="flex-1">
          {/* Tag pills at the top */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map(tag => {
                const c = getTagColor(tag);
                return (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="border-transparent gap-1 px-2 py-0.5 text-xs font-medium"
                    style={{ backgroundColor: c.bg, color: c.text }}
                  >
                    <span className="size-1.5 rounded-full" style={{ backgroundColor: c.dot }} />
                    {tag}
                  </Badge>
                );
              })}
            </div>
          )}
          {alumnus.headline && (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted mb-1 mt-2.5">{t('detail.headline')}</div>
              <div className="mb-1 text-sm">{highlightByTags(alumnus.headline, alumnus.tags)}</div>
            </>
          )}
          {alumnus.about && (
            <AboutSection about={alumnus.about} t={t} />
          )}
          {edu.schoolName && (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted mb-1 mt-2.5">{t('detail.ctuAffiliation')}</div>
              <div className="text-sm">
                <span className="font-medium">{edu.schoolName}</span>
                {(edu.startDate || edu.endDate) && (
                  <span className="text-muted-foreground">{' '}({edu.startDate}{edu.startDate && edu.endDate ? ' \u2013 ' : ''}{edu.endDate})</span>
                )}
              </div>
            </>
          )}
          {exp.length > 0 && (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted mb-1 mt-2.5">{t('detail.experience')}</div>
              {exp.map((e, i) => (
                <div key={i} className="text-sm leading-relaxed">
                  <span className="font-medium">{highlightByTags(e.position, alumnus.tags)}</span>
                  {e.companyName && <span className="text-secondary-foreground"> @ {highlightByTags(e.companyName, alumnus.tags) || e.companyName}</span>}
                  {(e.duration || e.startDate) && (
                    <span className="text-muted-foreground">
                      {' '}({e.duration}{e.startDate ? `, ${e.startDate}${e.endDate ? ` \u2013 ${e.endDate}` : ''}` : ''})
                    </span>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {/* Right: comments panel */}
        <CommentsPanel
          alumniId={alumnus.id} comments={comments} loading={loadingComments} t={t}
          onAdd={onAddComment} onUpdate={onUpdateComment} onDelete={onDeleteComment}
          editingComment={editingComment} setEditingComment={setEditingComment}
          addingCommentFor={addingCommentFor} setAddingCommentFor={setAddingCommentFor}
          newCommentText={newCommentText} setNewCommentText={setNewCommentText}
          currentUserId={currentUserId}
        />
      </div>
    </TableCell>
  );
}

function AboutSection({ about, t }: { about: string; t: TFunction }) {
  const [showAbout, setShowAbout] = useState(false);
  return (
    <>
      <button
        type="button"
        className="mt-2.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted hover:text-ctu-text-secondary transition-colors"
        onClick={() => setShowAbout(!showAbout)}
        aria-expanded={showAbout}
      >
        <ChevronRight
          className={cn('h-3 w-3 transition-transform', showAbout && 'rotate-90')}
          aria-hidden
        />
        {t('detail.about')}
      </button>
      {showAbout && (
        <div className="mt-1 mb-1 text-sm whitespace-pre-wrap leading-snug">{about}</div>
      )}
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function AlumniPage() {
  const { t } = useTranslation('alumni');
  const [alumni, setAlumni] = useState<AlumniData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [filterRules, setFilterRules] = useState<FilterRule[]>([emptyRule()]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [togglingStars, setTogglingStars] = useState<Set<number>>(new Set());

  // Tag filters
  const [activeTagFilters, setActiveTagFilters] = useState<Set<string>>(new Set());

  // Comments state
  const [commentsCache, setCommentsCache] = useState<Record<number, AlumniComment[]>>({});
  const [loadingComments, setLoadingComments] = useState<Set<number>>(new Set());
  const [editingComment, setEditingComment] = useState<EditingComment | null>(null);
  const [newCommentText, setNewCommentText] = useState<string>('');
  const [addingCommentFor, setAddingCommentFor] = useState<number | null>(null);

  const currentUserId = 'shared';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getAllAlumni();
        setAlumni(data.alumni || []);
      } catch (err: any) {
        setError(err.message || t('error'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect all unique tags
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    alumni.forEach(a => (a.tags || []).forEach(tag => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, [alumni]);

  const toggleTagFilter = useCallback((tag: string) => {
    setActiveTagFilters(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  // Load comments when a row is expanded
  const loadComments = useCallback(async (alumniId: number) => {
    if (commentsCache[alumniId]) return;
    setLoadingComments(prev => new Set(prev).add(alumniId));
    try {
      const data = await getAlumniComments(alumniId);
      setCommentsCache(prev => ({ ...prev, [alumniId]: data.comments || [] }));
    } catch {
      setCommentsCache(prev => ({ ...prev, [alumniId]: [] }));
    } finally {
      setLoadingComments(prev => {
        const next = new Set(prev);
        next.delete(alumniId);
        return next;
      });
    }
  }, [commentsCache]);

  const handleAddComment = useCallback(async (alumniId: number, text: string) => {
    if (!text.trim()) return;
    try {
      const comment = await createAlumniComment(alumniId, text);
      setCommentsCache(prev => ({
        ...prev,
        [alumniId]: [comment, ...(prev[alumniId] || [])],
      }));
      setAlumni(prev => prev.map(a => a.id === alumniId ? { ...a, commentCount: (a.commentCount || 0) + 1 } : a));
    } catch { /* ignore */ }
  }, []);

  const handleUpdateComment = useCallback(async (commentId: number, text: string) => {
    try {
      const updated = await updateAlumniComment(commentId, text);
      setCommentsCache(prev => {
        const cache = { ...prev };
        const key = updated.alumniId;
        if (cache[key]) {
          cache[key] = cache[key].map(c => c.id === commentId ? updated : c);
        }
        return cache;
      });
    } catch { /* ignore */ }
  }, []);

  const handleDeleteComment = useCallback(async (commentId: number, alumniId: number) => {
    try {
      await deleteAlumniComment(commentId);
      setCommentsCache(prev => ({
        ...prev,
        [alumniId]: (prev[alumniId] || []).filter(c => c.id !== commentId),
      }));
      setAlumni(prev => prev.map(a => a.id === alumniId ? { ...a, commentCount: Math.max(0, (a.commentCount || 0) - 1) } : a));
    } catch { /* ignore */ }
  }, []);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIndicator = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  const handleToggleStar = useCallback(async (alumniId: number) => {
    if (togglingStars.has(alumniId)) return;
    setTogglingStars((prev) => new Set(prev).add(alumniId));
    try {
      const result = await toggleAlumniStar(alumniId);
      setAlumni((prev) =>
        prev.map((a) => a.id === alumniId ? { ...a, starred: result.starred } : a)
      );
    } catch {
      // silently ignore
    } finally {
      setTogglingStars((prev) => {
        const next = new Set(prev);
        next.delete(alumniId);
        return next;
      });
    }
  }, [togglingStars]);

  const handleExpandRow = useCallback((globalIdx: number, alumniId: number) => {
    const willExpand = expandedRow !== globalIdx;
    setExpandedRow(willExpand ? globalIdx : null);
    if (willExpand) {
      loadComments(alumniId);
    }
  }, [expandedRow, loadComments]);

  const handleCommentIconClick = useCallback((globalIdx: number, alumniId: number) => {
    handleExpandRow(globalIdx, alumniId);
  }, [handleExpandRow]);

  const filteredAndSorted = useMemo(() => {
    let data = alumni;

    // Global search (all fields, diacritics-insensitive)
    const searchNorm = normalize(globalSearch.trim());
    if (searchNorm) {
      data = data.filter(item => matchesGlobalSearch(item, searchNorm));
    }

    // Column-specific rules
    data = applyRules(data, filterRules);

    // Apply tag filters (OR logic)
    if (activeTagFilters.size > 0) {
      data = data.filter(item =>
        (item.tags || []).some(tag => activeTagFilters.has(tag))
      );
    }

    if (sortConfig.key) {
      const sortKey = sortConfig.key;
      const isAsc = sortConfig.direction === 'asc';
      data = [...data].sort((a, b) => {
        if (sortKey === 'starred') {
          const aV = a.starred ? 0 : 1;
          const bV = b.starred ? 0 : 1;
          return isAsc ? aV - bV : bV - aV;
        }
        let aVal, bVal;
        switch (sortKey) {
          case 'firstName':
          case 'lastName':
          case 'currentCompany':
          case 'location':
            aVal = (a[sortKey] || '').toLowerCase();
            bVal = (b[sortKey] || '').toLowerCase();
            break;
          case 'position':
            aVal = getCurrentPosition(a.experience).toLowerCase();
            bVal = getCurrentPosition(b.experience).toLowerCase();
            break;
          case 'connectionsCount':
          case 'followerCount':
            aVal = Number(a[sortKey]) || 0;
            bVal = Number(b[sortKey]) || 0;
            break;
          case 'endYear': {
            const aYear = getEndYear(a.education);
            const bYear = getEndYear(b.education);
            if (!aYear && !bYear) return 0;
            if (!aYear) return 1;
            if (!bYear) return -1;
            aVal = aYear;
            bVal = bYear;
            break;
          }
          default:
            aVal = a[sortKey] || '';
            bVal = b[sortKey] || '';
        }
        if (aVal < bVal) return isAsc ? -1 : 1;
        if (aVal > bVal) return isAsc ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [alumni, globalSearch, filterRules, sortConfig, activeTagFilters]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [globalSearch, filterRules, activeTagFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / ROWS_PER_PAGE));
  const pageStart = (currentPage - 1) * ROWS_PER_PAGE;
  const pageData = filteredAndSorted.slice(pageStart, pageStart + ROWS_PER_PAGE);

  const activeRuleCount = filterRules.filter(isRuleComplete).length;

  const columnLabel = useCallback((key: string): string => {
    const tKey = {
      firstName: 'table.name',
      lastName: 'table.surname',
      currentCompany: 'table.company',
      position: 'table.position',
      location: 'table.location',
      connectionsCount: 'table.connections',
      followerCount: 'table.followers',
      endYear: 'table.endYear',
    }[key];
    return tKey ? t(tKey) : key;
  }, [t]);

  function paginationItems(): (number | 'ellipsis')[] {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const set = new Set<number>([1, totalPages, currentPage]);
    [-1, 1].forEach((o) => set.add(currentPage + o));
    if (currentPage <= 3) [2, 3, 4].forEach((n) => set.add(n));
    if (currentPage >= totalPages - 2)
      [totalPages - 1, totalPages - 2, totalPages - 3].forEach((n) => set.add(n));
    const nums = Array.from(set)
      .filter((n) => n >= 1 && n <= totalPages)
      .sort((a, b) => a - b);
    const out: (number | 'ellipsis')[] = [];
    for (let i = 0; i < nums.length; i++) {
      out.push(nums[i]);
      if (i < nums.length - 1 && nums[i + 1] - nums[i] > 1) out.push('ellipsis');
    }
    return out;
  }

  const headCellClass =
    'px-3 py-2.5 text-left font-bold text-xs text-ctu-text-secondary whitespace-nowrap select-none';
  const sortableHeadClass = `${headCellClass} cursor-pointer hover:bg-ctu-bg/70`;
  const cellClass = 'px-3 py-2 text-sm text-ctu-text';

  return (
    <PageContainer>
      <PageContent className="max-w-[1500px]">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && (
          <Card
            className="border-ctu-border shadow-none"
            aria-busy="true"
            aria-label={t('loading')}
          >
            <CardContent className="px-6 pt-5 pb-4 flex flex-col gap-4">
              <Skeleton className="h-9 w-full max-w-md" />
              <div className="flex flex-col gap-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && !error && (
          <>
            {/* Filters */}
            <Card className="border-ctu-border shadow-none mb-6">
              <CardContent className="px-6 pt-5 pb-5 flex flex-col gap-4">
                <div className="relative w-full">
                  <Search
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ctu-text-muted"
                    aria-hidden
                  />
                  <Input
                    type="search"
                    value={globalSearch}
                    onChange={e => setGlobalSearch(e.target.value)}
                    placeholder={t('filter.globalPlaceholder')}
                    className="pl-8"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                    {t('filter.andNote')}
                  </div>
                  <div className="w-full max-w-xl">
                    <ColumnFilterBuilder
                      rules={filterRules}
                      onChange={setFilterRules}
                      t={t}
                      columns={COLUMN_DEFS}
                      columnLabel={columnLabel}
                    />
                  </div>
                </div>

                <TagBar
                  allTags={allTags}
                  activeTagFilters={activeTagFilters}
                  onToggle={toggleTagFilter}
                  t={t}
                />
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="border-ctu-border shadow-none">
              <CardContent className="px-0 pt-4 pb-2">
                <div className="px-6 mb-3 text-xs text-ctu-text-muted tabular-nums">
                  {t('results.showing', { count: filteredAndSorted.length })}
                  {(globalSearch.trim() || activeRuleCount > 0 || activeTagFilters.size > 0) &&
                    filteredAndSorted.length !== alumni.length && (
                      <span className="ml-1.5">
                        {t('results.filtered', { total: alumni.length })}
                      </span>
                    )}
                </div>

                <div className="overflow-x-auto">
                  <Table className="text-sm">
                  <TableHeader>
                    <TableRow className="bg-ctu-bg">
                      <TableHead className={sortableHeadClass} onClick={() => handleSort('firstName')}>{t('table.name')}{getSortIndicator('firstName')}</TableHead>
                      <TableHead className={sortableHeadClass} onClick={() => handleSort('lastName')}>{t('table.surname')}{getSortIndicator('lastName')}</TableHead>
                      <TableHead className={`${headCellClass} text-center`}>{t('table.linkedin')}</TableHead>
                      <TableHead className={sortableHeadClass} onClick={() => handleSort('currentCompany')}>{t('table.company')}{getSortIndicator('currentCompany')}</TableHead>
                      <TableHead className={sortableHeadClass} onClick={() => handleSort('position')}>{t('table.position')}{getSortIndicator('position')}</TableHead>
                      <TableHead className={sortableHeadClass} onClick={() => handleSort('location')}>{t('table.location')}{getSortIndicator('location')}</TableHead>
                      <TableHead className={`${headCellClass} text-center`}>{t('table.tags')}</TableHead>
                      <TableHead className={`${sortableHeadClass} text-center`} onClick={() => handleSort('connectionsCount')}>{t('table.connections')}{getSortIndicator('connectionsCount')}</TableHead>
                      <TableHead className={`${sortableHeadClass} text-center`} onClick={() => handleSort('followerCount')}>{t('table.followers')}{getSortIndicator('followerCount')}</TableHead>
                      <TableHead className={`${sortableHeadClass} text-center`} onClick={() => handleSort('endYear')}>{t('table.endYear')}{getSortIndicator('endYear')}</TableHead>
                      <TableHead className={`${sortableHeadClass} text-center`} onClick={() => handleSort('starred')}>{t('table.starred')}{getSortIndicator('starred')}</TableHead>
                      <TableHead className={`${headCellClass} text-center`}>{t('table.comments')}</TableHead>
                      <TableHead className={`${headCellClass} text-center w-10`} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={COL_SPAN} className="px-3 py-10 text-center text-sm italic text-ctu-text-muted">
                          {t('detail.noInfo')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      pageData.map((a, idx) => {
                        const globalIdx = pageStart + idx;
                        const isExpanded = expandedRow === globalIdx;
                        const tags = a.tags || [];
                        return (
                          <React.Fragment key={globalIdx}>
                            <TableRow
                              className={cn(
                                'transition-colors hover:bg-muted/40',
                                a.starred && 'bg-amber-50/60',
                              )}
                            >
                              <TableCell className={cellClass}>{a.firstName}</TableCell>
                              <TableCell className={`${cellClass} font-medium`}>{a.lastName}</TableCell>
                              <TableCell className={`${cellClass} text-center`}>
                                {a.linkedinUrl ? (
                                  <a
                                    href={a.linkedinUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-medium text-primary no-underline hover:underline"
                                  >
                                    LinkedIn
                                  </a>
                                ) : '\u2013'}
                              </TableCell>
                              <TableCell className={cellClass}>{a.currentCompany ? highlightByTags(a.currentCompany, a.tags) : '\u2013'}</TableCell>
                              <TableCell className={cellClass}>{highlightByTags(getCurrentPosition(a.experience), a.tags) || '\u2013'}</TableCell>
                              <TableCell className={cellClass}>{a.location || '\u2013'}</TableCell>
                              <TableCell className={`${cellClass} text-center`}>
                                {tags.length > 0 ? (
                                  <div className="flex flex-wrap justify-center gap-0.5">
                                    {tags.map(tag => (
                                      <span
                                        key={tag}
                                        title={tag}
                                        className="inline-block size-2.5 rounded-full"
                                        style={{ backgroundColor: getTagColor(tag).dot }}
                                      />
                                    ))}
                                  </div>
                                ) : '\u2013'}
                              </TableCell>
                              <TableCell className={`${cellClass} text-center tabular-nums`}>{a.connectionsCount || 0}</TableCell>
                              <TableCell className={`${cellClass} text-center tabular-nums`}>{a.followerCount || 0}</TableCell>
                              <TableCell className={`${cellClass} text-center tabular-nums`}>{getEndYear(a.education) || '\u2013'}</TableCell>
                              <TableCell className={`${cellClass} text-center`}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    'h-8 w-8',
                                    a.starred
                                      ? 'text-yellow-500 hover:text-yellow-600'
                                      : 'text-ctu-text-muted/40 hover:text-ctu-text-muted',
                                  )}
                                  title={a.starred ? t('table.unstar') : t('table.star')}
                                  disabled={togglingStars.has(a.id)}
                                  onClick={() => handleToggleStar(a.id)}
                                >
                                  <Star
                                    className="h-4 w-4"
                                    fill={a.starred ? 'currentColor' : 'none'}
                                  />
                                </Button>
                              </TableCell>
                              <TableCell className={`${cellClass} text-center`}>
                                <button
                                  type="button"
                                  className={cn(
                                    'inline-flex items-center justify-center rounded-md p-1 transition-colors',
                                    (a.commentCount || 0) > 0
                                      ? 'text-primary hover:text-primary/80'
                                      : 'text-ctu-text-muted/40 hover:text-ctu-text-muted',
                                  )}
                                  title={t('table.comments')}
                                  onClick={() => handleCommentIconClick(globalIdx, a.id)}
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </button>
                              </TableCell>
                              <TableCell className={`${cellClass} text-center`}>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center rounded-md p-1 text-ctu-text-muted hover:text-ctu-text-secondary transition-colors"
                                  aria-expanded={isExpanded}
                                  onClick={() => handleExpandRow(globalIdx, a.id)}
                                >
                                  <ChevronRight
                                    className={cn(
                                      'h-4 w-4 transition-transform',
                                      isExpanded && 'rotate-90',
                                    )}
                                    aria-hidden
                                  />
                                </button>
                              </TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow>
                                <ExpandedContent
                                  alumnus={a} t={t}
                                  comments={commentsCache[a.id] || []}
                                  loadingComments={loadingComments.has(a.id)}
                                  onAddComment={handleAddComment}
                                  onUpdateComment={handleUpdateComment}
                                  onDeleteComment={handleDeleteComment}
                                  editingComment={editingComment}
                                  setEditingComment={setEditingComment}
                                  addingCommentFor={addingCommentFor}
                                  setAddingCommentFor={setAddingCommentFor}
                                  newCommentText={newCommentText}
                                  setNewCommentText={setNewCommentText}
                                  currentUserId={currentUserId}
                                />
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredAndSorted.length > ROWS_PER_PAGE && (
                <div className="flex flex-col items-center gap-2 px-6 pt-4 pb-2 sm:flex-row sm:justify-between">
                  <span className="text-xs text-ctu-text-muted tabular-nums">
                    {pageStart + 1}{'\u2013'}
                    {Math.min(pageStart + ROWS_PER_PAGE, filteredAndSorted.length)} /{' '}
                    {filteredAndSorted.length}
                  </span>
                  <Pagination className="w-auto mx-0">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          aria-disabled={currentPage <= 1}
                          className={
                            currentPage <= 1
                              ? 'pointer-events-none opacity-40'
                              : ''
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1) setCurrentPage(currentPage - 1);
                          }}
                        />
                      </PaginationItem>

                      {paginationItems().map((it, idx) =>
                        it === 'ellipsis' ? (
                          <PaginationItem key={`e-${idx}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={it}>
                            <PaginationLink
                              href="#"
                              isActive={it === currentPage}
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentPage(it);
                              }}
                            >
                              {it}
                            </PaginationLink>
                          </PaginationItem>
                        ),
                      )}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          aria-disabled={currentPage >= totalPages}
                          className={
                            currentPage >= totalPages
                              ? 'pointer-events-none opacity-40'
                              : ''
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                          }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
          </>
        )}
      </PageContent>
    </PageContainer>
  );
}
